import { useEffect, useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Download, Upload, Pencil, Trash2, Mail, Phone, MessageSquare, CheckCircle2, XCircle, Copy, BookOpen, FileText, Globe, Link2Off, Orbit, History } from "lucide-react";
import { EmailLogDialog } from "@/components/EmailLogDialog";
import Customer360 from "@/components/Customer360";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { usePager, Pager } from "@/components/Pager";
import { API_URL, inr, fmtDate } from "@/lib/api";
import { portalLoginUrl } from "@/lib/hostRouting";

const CREDIT_TERMS = [
  { value: "Advance", label: "Advance Payment (No Credit)" },
  { value: "15", label: "15 Days" },
  { value: "30", label: "30 Days" },
  { value: "45", label: "45 Days" },
  { value: "60", label: "60 Days" },
  { value: "90", label: "90 Days" },
];

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const GST_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
function validGSTIN(g) {
  g = (g || "").trim().toUpperCase();
  if (!GSTIN_RE.test(g)) return false;
  let total = 0;
  for (let i = 0; i < 14; i++) {
    const p = GST_CHARS.indexOf(g[i]) * (i % 2 ? 2 : 1);
    total += Math.floor(p / 36) + (p % 36);
  }
  return GST_CHARS[(36 - (total % 36)) % 36] === g[14];
}

const empty = {
  company_name: "", contact_person: "", mobile: "", email: "", contacts: [],
  gst_registered: true, gst_number: "",
  billing_line1: "", billing_line2: "", billing_city: "", billing_state: "", billing_pincode: "",
  shipping_line1: "", shipping_line2: "", shipping_city: "", shipping_state: "", shipping_pincode: "",
  credit_terms: "Advance", notes: "",
};

import { LedgerRangeBar } from "@/components/LedgerRangeBar";

export default function Customers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useUrlSearch();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerCustomer, setLedgerCustomer] = useState(null);
  const [lr, setLr] = useState({ key: "all", from: "", to: "" });
  const [portalFor, setPortalFor] = useState(null); // {customer, info, password}
  const [c360, setC360] = useState(null);
  const [emailLogFor, setEmailLogFor] = useState(null);

  const openPortal = async (c) => {
    try {
      const r = await api.get(`/portal-admin/${c.id}/access`);
      setPortalFor({ customer: c, info: r.data, password: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const savePortal = async (enabled) => {
    try {
      await api.post(`/portal-admin/${portalFor.customer.id}/access`, { enabled, password: portalFor.password });
      toast.success(enabled ? "Portal access enabled" : "Portal access disabled");
      const r = await api.get(`/portal-admin/${portalFor.customer.id}/access`);
      setPortalFor({ ...portalFor, info: r.data, password: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendWelcome = async () => {
    try {
      const pw = portalFor.password;
      if (pw) {
        await api.post(`/portal-admin/${portalFor.customer.id}/access`, { enabled: true, password: pw });
      }
      await api.post(`/portal-admin/${portalFor.customer.id}/send-welcome`, { password: pw });
      toast.success(`Welcome email sent to ${portalFor.customer.email}`);
      const r = await api.get(`/portal-admin/${portalFor.customer.id}/access`);
      setPortalFor({ ...portalFor, info: r.data, password: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openLedger = async (c) => {
    try {
      const r = await api.get(`/payments/ledger/${c.id}`);
      setLr({ key: "all", from: "", to: "" });
      setLedger(r.data); setLedgerCustomer(c);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyLedgerRange = async (next) => {
    setLr(next);
    try {
      const r = await api.get(`/payments/ledger/${ledgerCustomer.id}?date_from=${next.from}&date_to=${next.to}`);
      setLedger(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const shareStatement = async () => {
    try {
      const r = await api.get(`/payments/ledger/${ledgerCustomer.id}/share-link`);
      const link = `${API_URL}/public/ledger/${r.data.share_token}/pdf`;
      const msg = `Dear ${ledgerCustomer.company_name},\n\nPlease find your ledger statement with Hi-Tech Silico.\nTotal Invoiced: ${inr(ledger.total_invoiced)}\nTotal Received: ${inr(ledger.total_paid)}\nBalance Due: ${inr(ledger.balance)}\n\nView / download the statement here: ${link}\n\nRegards,\nHi-Tech Silico\n+91 8888837157`;
      const digits = (ledgerCustomer.mobile || "").replace(/\D/g, "");
      const phone = digits.length === 10 ? `91${digits}` : digits;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revokeStatementShare = async () => {
    if (!window.confirm("Revoke the ledger share link? Anyone with the old link will lose access.")) return;
    try {
      await api.delete(`/payments/ledger/${ledgerCustomer.id}/share-link`);
      toast.success("Ledger share link revoked — old links no longer work");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = () => api.get("/customers", { params: { q } }).then((r) => setList(r.data));
  const pager = usePager(list);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [impOpen, setImpOpen] = useState(false);
  const [impBusy, setImpBusy] = useState(false);
  const [impPreview, setImpPreview] = useState(null);
  const [impRes, setImpRes] = useState({});

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/customers/import/template", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "customer_import_template.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadPreview = async (file) => {
    if (!file) return;
    setImpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/customers/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImpPreview(r.data);
      setImpRes(Object.fromEntries(r.data.duplicates.map((d) => [d.existing_id, "keep"])));
    } catch (e) { toast.error(formatApiError(e)); }
    setImpBusy(false);
  };

  const commitImport = async () => {
    setImpBusy(true);
    try {
      const updates = impPreview.duplicates.filter((d) => impRes[d.existing_id] === "replace")
        .map((d) => ({ id: d.existing_id, data: d.incoming }));
      const r = await api.post("/customers/import/commit", { new: impPreview.new, updates });
      toast.success(`Imported: ${r.data.created} created, ${r.data.updated} updated${r.data.skipped ? `, ${r.data.skipped} skipped` : ""}`);
      setImpOpen(false); setImpPreview(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setImpBusy(false);
  };

  useEffect(() => {
    const lid = searchParams.get("ledger");
    if (!lid || !list.length) return;
    const c = list.find((x) => x.id === lid);
    if (c) { openLedger(c); setSearchParams({}, { replace: true }); }
    // eslint-disable-next-line
  }, [list]);

  const save = async () => {
    if (form.gst_registered && form.gst_number && !validGSTIN(form.gst_number)) {
      toast.error("GST number is invalid — please check it");
      return;
    }
    try {
      if (editing) await api.put(`/customers/${editing}`, form);
      else await api.post("/customers", form);
      toast.success("Customer saved");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete customer?")) return;
    await api.delete(`/customers/${id}`); toast.success("Deleted"); load();
  };
  const edit = (c) => { setEditing(c.id); setForm({ ...empty, ...c }); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  const copyBilling = () => setForm({
    ...form,
    shipping_line1: form.billing_line1, shipping_line2: form.billing_line2,
    shipping_city: form.billing_city, shipping_state: form.billing_state,
    shipping_pincode: form.billing_pincode,
  });

  const gstStatus = form.gst_registered && form.gst_number
    ? (validGSTIN(form.gst_number) ? "valid" : "invalid") : null;

  return (
    <div className="space-y-4" data-testid="customers-page">
      <PageHeader title="Customers" subtitle="Manage buyer accounts, GST and credit terms" />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input placeholder="Search company, contact, mobile…" value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="customer-search" className="pl-8" />
        </div>
        <a href={`${API_URL}/customers/export/excel`} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50 text-slate-700"
           data-testid="customer-export">
          <Download size={14} /> Excel
        </a>
        <Button variant="outline" onClick={() => { setImpPreview(null); setImpOpen(true); }} className="border-slate-300 text-slate-700"
                data-testid="customer-import">
          <Upload size={14} className="mr-1.5" /> Import
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="customer-new" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">
              <Plus size={14} className="mr-1" /> New Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
            <DialogHeader><DialogTitle>{editing ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Company Name *" v={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} tid="c-name" />
              <Field label="Contact Person" v={form.contact_person} onChange={(v) => setForm({ ...form, contact_person: v })} tid="c-contact" />
              <Field label="Mobile" v={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} tid="c-mobile" />
              <Field label="Email" v={form.email} onChange={(v) => setForm({ ...form, email: v })} tid="c-email" />

              <div className="sm:col-span-2 border border-slate-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Contact Persons (purchase managers etc.)</Label>
                  <Button type="button" variant="outline" size="sm" data-testid="c-add-contact"
                          onClick={() => setForm({ ...form, contacts: [...(form.contacts || []), { name: "", designation: "", email: "", mobile: "" }] })}>
                    <Plus size={12} className="mr-1" /> Add contact
                  </Button>
                </div>
                {(form.contacts || []).length === 0 && (
                  <div className="text-xs text-slate-400">All emails go to the primary email above. Add extra contacts here — they'll automatically receive a copy of receipts &amp; reminders.</div>
                )}
                {(form.contacts || []).map((ct, idx) => (
                  <div key={idx} className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1.2fr_1fr_auto] gap-2 items-center" data-testid={`c-contact-row-${idx}`}>
                    <Input placeholder="Name" value={ct.name} data-testid={`c-contact-name-${idx}`}
                           onChange={(e) => setForm({ ...form, contacts: form.contacts.map((x, i) => i === idx ? { ...x, name: e.target.value } : x) })} />
                    <Input placeholder="Designation" value={ct.designation}
                           onChange={(e) => setForm({ ...form, contacts: form.contacts.map((x, i) => i === idx ? { ...x, designation: e.target.value } : x) })} />
                    <Input placeholder="Email" type="email" value={ct.email} data-testid={`c-contact-email-${idx}`}
                           onChange={(e) => setForm({ ...form, contacts: form.contacts.map((x, i) => i === idx ? { ...x, email: e.target.value } : x) })} />
                    <Input placeholder="Mobile" value={ct.mobile}
                           onChange={(e) => setForm({ ...form, contacts: form.contacts.map((x, i) => i === idx ? { ...x, mobile: e.target.value } : x) })} />
                    <button type="button" className="text-slate-400 hover:text-red-500 p-1.5" title="Remove contact" data-testid={`c-contact-remove-${idx}`}
                            onClick={() => setForm({ ...form, contacts: form.contacts.filter((_, i) => i !== idx) })}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="sm:col-span-2 flex items-center gap-2 pt-1">
                <Checkbox id="gst-reg" checked={!form.gst_registered}
                          onCheckedChange={(v) => setForm({ ...form, gst_registered: !v, gst_number: v ? "" : form.gst_number })}
                          data-testid="c-gst-unregistered" />
                <Label htmlFor="gst-reg" className="text-sm text-slate-600 cursor-pointer">Customer is not registered for GST</Label>
              </div>
              {form.gst_registered && (
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">GST Number</Label>
                  <Input value={form.gst_number}
                         onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })}
                         maxLength={15} placeholder="e.g. 27AAPFU0939F1ZV" className="mt-1.5 font-mono" data-testid="c-gst" />
                  {gstStatus === "valid" && (
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1" data-testid="c-gst-valid">
                      <CheckCircle2 size={12} /> Valid GSTIN
                    </p>
                  )}
                  {gstStatus === "invalid" && (
                    <p className="text-xs text-rose-600 mt-1 flex items-center gap-1" data-testid="c-gst-invalid">
                      <XCircle size={12} /> Invalid GSTIN — check format & digits
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Credit Terms</Label>
                <Select value={form.credit_terms} onValueChange={(v) => setForm({ ...form, credit_terms: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="c-terms"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CREDIT_TERMS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <AddressBlock title="Billing Address" prefix="billing" form={form} setForm={setForm} />
              <AddressBlock title="Shipping Address" prefix="shipping" form={form} setForm={setForm}
                            action={
                              <button type="button" onClick={copyBilling} data-testid="c-copy-billing"
                                      className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
                                <Copy size={11} /> Same as billing
                              </button>
                            } />

              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" rows={2} data-testid="c-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="c-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Company</th>
              <th className="text-left px-4 py-2.5 font-semibold hidden md:table-cell">Contact</th>
              <th className="text-left px-4 py-2.5 font-semibold hidden lg:table-cell">GST</th>
              <th className="text-left px-4 py-2.5 font-semibold hidden lg:table-cell">Terms</th>
              <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`c-row-${c.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800">{c.company_name}</div>
                  <div className="text-xs text-slate-500">
                    {c.contact_person}
                    {(c.contacts || []).filter((x) => x.name || x.email).length > 0 && (
                      <span className="ml-1.5 inline-flex items-center rounded-full bg-[#3B82F6]/5 border border-[#3B82F6]/10 px-1.5 py-0.5 text-[10px] text-[#3B82F6]"
                            title={(c.contacts || []).map((x) => `${x.name}${x.designation ? ` (${x.designation})` : ""}${x.email ? ` — ${x.email}` : ""}`).join("\n")}
                            data-testid={`c-contacts-badge-${c.id}`}>
                        +{(c.contacts || []).filter((x) => x.name || x.email).length} contacts
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-700">
                  <div className="flex items-center gap-2 text-xs">
                    {c.mobile && <a href={`tel:${c.mobile}`} className="inline-flex items-center gap-1 hover:text-blue-600"><Phone size={12} />{c.mobile}</a>}
                  </div>
                  <div className="flex items-center gap-2 text-xs mt-1">
                    {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-blue-600"><Mail size={12} />{c.email}</a>}
                    {c.mobile && <a href={`https://wa.me/${c.mobile.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800"><MessageSquare size={12} />WhatsApp</a>}
                  </div>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell text-slate-700 font-mono text-xs">
                  {c.gst_registered === false ? <span className="font-sans text-slate-400">Unregistered</span> : (c.gst_number || "-")}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell text-slate-700">
                  {c.credit_terms === "Advance" ? "Advance Payment" : `${c.credit_terms} days`}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openPortal(c)} className="text-slate-500 hover:text-emerald-600 mr-2" title="Portal access" data-testid={`c-portal-${c.id}`}><Globe size={14} /></button>
                  <button onClick={() => setEmailLogFor(c)} className="text-slate-500 hover:text-[#3B82F6] mr-2" title="Email timeline (all sender accounts)" data-testid={`c-emaillog-${c.id}`}><History size={14} /></button>
                  <button onClick={() => setC360(c)} className="text-slate-500 hover:text-[#FC5F5F] mr-2" title="Customer 360° view" data-testid={`c-360-${c.id}`}><Orbit size={14} /></button>
                  <button onClick={() => openLedger(c)} className="text-slate-500 hover:text-indigo-600 mr-2" title="Ledger & invoices" data-testid={`c-ledger-${c.id}`}><BookOpen size={14} /></button>
                  <button onClick={() => edit(c)} className="text-slate-500 hover:text-blue-600 mr-2" data-testid={`c-edit-${c.id}`}><Pencil size={14} /></button>
                  <button onClick={() => del(c.id)} className="text-slate-500 hover:text-rose-600" data-testid={`c-del-${c.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={5} className="text-center text-sm text-slate-500 py-10">No customers yet.</td></tr>
            )}
          </tbody>
        </table>
        <Pager pager={pager} />
      </div>

      <Customer360 customer={c360} onClose={() => setC360(null)} />
      <EmailLogDialog customer={emailLogFor} onClose={() => setEmailLogFor(null)} />

      <Dialog open={!!ledger} onOpenChange={(v) => !v && setLedger(null)}>
        <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center justify-between gap-2 pr-8">
              <span>Customer Ledger — {ledgerCustomer?.company_name}</span>
              <span className="flex items-center gap-2">
                <a href={`${API_URL}/payments/ledger/${ledgerCustomer?.id}/pdf?date_from=${lr.from}&date_to=${lr.to}`} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 text-xs font-medium border border-slate-200 rounded-full px-3.5 py-1.5 text-slate-600 hover:text-[#3B82F6] hover:border-slate-400 transition-colors duration-200"
                   data-testid="cl-statement-pdf">
                  <FileText size={13} /> Statement PDF
                </a>
                <button onClick={shareStatement}
                        className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white transition-colors duration-200"
                        data-testid="cl-share-whatsapp">
                  <MessageSquare size={13} /> Share on WhatsApp
                </button>
                <button onClick={revokeStatementShare}
                        className="inline-flex items-center gap-1.5 text-xs font-medium border border-slate-200 rounded-full px-3.5 py-1.5 text-slate-500 hover:text-red-600 hover:border-red-200 transition-colors duration-200"
                        data-testid="cl-revoke-share">
                  <Link2Off size={13} /> Revoke Link
                </button>
              </span>
            </DialogTitle>
          </DialogHeader>
          {ledger && (
            <>
              <LedgerRangeBar lr={lr} onApply={applyLedgerRange} />
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="border border-slate-200 rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Invoiced</p>
                  <p className="text-lg font-bold tabular-nums" data-testid="cl-total-invoiced">{inr(ledger.total_invoiced)}</p>
                </div>
                <div className="border border-slate-200 rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Received</p>
                  <p className="text-lg font-bold tabular-nums text-emerald-700" data-testid="cl-total-paid">{inr(ledger.total_paid)}</p>
                </div>
                <div className="border border-slate-200 rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Balance Due</p>
                  <p className={`text-lg font-bold tabular-nums ${ledger.balance > 0 ? "text-rose-600" : "text-slate-800"}`} data-testid="cl-balance">{inr(ledger.balance)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start mt-2">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Invoices</p>
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full text-sm" data-testid="cl-invoices">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-2">Invoice No.</th>
                          <th className="text-left px-3 py-2">Date</th>
                          <th className="text-right px-3 py-2">Amount</th>
                          <th className="text-right px-3 py-2">Received</th>
                          <th className="text-left px-3 py-2">Status</th>
                          <th className="text-right px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledger.invoices.map((i) => (
                          <tr key={i.id} className={`border-t border-slate-100 ${i.status === "Overdue" ? "bg-rose-50/60" : ""}`}>
                            <td className="px-3 py-2 font-mono text-xs">{i.number}</td>
                            <td className="px-3 py-2">{fmtDate(i.date)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{inr(i.total)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{inr(i.paid)}</td>
                            <td className="px-3 py-2">
                              <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 whitespace-nowrap ${
                                i.status === "Paid" ? "bg-emerald-100 text-emerald-700"
                                : i.status === "Partial" ? "bg-amber-100 text-amber-700"
                                : i.status === "Overdue" ? "bg-rose-100 text-rose-700"
                                : "bg-slate-100 text-slate-600"}`} data-testid={`cl-inv-status-${i.id}`}>{i.status}</span>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <a href={`${API_URL}/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-blue-600 inline-flex" title="PDF" data-testid={`cl-inv-pdf-${i.id}`}><FileText size={13} /></a>
                            </td>
                          </tr>
                        ))}
                        {ledger.invoices.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-sm text-slate-500">No invoices for this customer yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Ledger Entries</p>
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full text-sm" data-testid="cl-entries">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-2">Date</th>
                          <th className="text-left px-3 py-2">Particulars</th>
                          <th className="text-right px-3 py-2">Debit</th>
                          <th className="text-right px-3 py-2">Credit</th>
                          <th className="text-right px-3 py-2">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledger.entries.map((e, idx) => (
                          <tr key={idx} className="border-t border-slate-100">
                            <td className="px-3 py-2 whitespace-nowrap">{fmtDate(e.date)}</td>
                            <td className="px-3 py-2 text-slate-700"><span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 mr-1.5 ${e.type === "Invoice" ? "bg-slate-100 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>{e.type}</span><span className="text-xs">{e.ref}</span></td>
                            <td className="px-3 py-2 text-right tabular-nums">{e.debit ? inr(e.debit) : "-"}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{e.credit ? inr(e.credit) : "-"}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">{inr(e.balance)}</td>
                          </tr>
                        ))}
                        {ledger.entries.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-sm text-slate-500">No transactions yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!portalFor} onOpenChange={(v) => !v && setPortalFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Globe size={15} className="text-emerald-600" /> Portal Access — {portalFor?.customer?.company_name}</DialogTitle>
          </DialogHeader>
          {portalFor && (
            <div className="space-y-4 text-sm">
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                <span className="text-xs text-slate-600">Status</span>
                <span className={`text-[10px] uppercase tracking-wider rounded px-2 py-0.5 ${portalFor.info.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`} data-testid="portal-status">
                  {portalFor.info.enabled ? "Enabled" : "Disabled"}</span>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                  {portalFor.info.has_password ? "Set New Password (optional)" : "Set Password (required to enable)"}</Label>
                <Input type="text" value={portalFor.password} onChange={(e) => setPortalFor({ ...portalFor, password: e.target.value })}
                       placeholder="Min 6 characters" className="mt-1.5" data-testid="portal-password" />
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Customer logs in with their <b>email ({portalFor.customer.email || "not set"})</b> or <b>mobile ({portalFor.customer.mobile || "not set"})</b> and this password at:
              </p>
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 min-w-0">
                <span className="text-xs font-mono truncate flex-1 min-w-0">{portalLoginUrl()}</span>
                <button onClick={() => { navigator.clipboard.writeText(portalLoginUrl()); toast.success("Link copied"); }}
                        className="text-slate-400 hover:text-blue-600" data-testid="portal-copy-link"><Copy size={13} /></button>
              </div>
              {portalFor.info.last_login && <p className="text-[11px] text-slate-400">Last login: {new Date(portalFor.info.last_login).toLocaleString()}</p>}
              <div className="flex gap-2">
                <Button onClick={() => savePortal(true)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="portal-enable">
                  {portalFor.info.enabled ? "Update" : "Enable Access"}</Button>
                {portalFor.info.enabled && (
                  <Button onClick={() => savePortal(false)} variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="portal-disable">Disable</Button>
                )}
              </div>
              {portalFor.info.enabled && (
                <Button onClick={sendWelcome} variant="outline" disabled={!portalFor.customer.email}
                        className="w-full border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="portal-send-welcome">
                  <Mail size={14} className="mr-1.5 text-[#FC5F5F]" />
                  {portalFor.customer.email
                    ? `Send Welcome Email${portalFor.password ? " (saves & includes new password)" : ""}`
                    : "Add customer email to send welcome"}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={impOpen} onOpenChange={(v) => { setImpOpen(v); if (!v) setImpPreview(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Import Customers from Excel</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={downloadTemplate} className="border-slate-300 text-slate-700" data-testid="import-template">
                <Download size={14} className="mr-1.5" /> Download Template
              </Button>
              <label className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50 text-slate-700 cursor-pointer">
                <Upload size={14} /> {impBusy ? "Reading…" : "Choose .xlsx file"}
                <input type="file" accept=".xlsx" className="hidden" data-testid="import-file"
                       onChange={(e) => { uploadPreview(e.target.files[0]); e.target.value = ""; }} />
              </label>
            </div>
            <p className="text-xs text-slate-500">Fill the template (one customer per row, <b>company_name</b> is required) and upload it. Nothing is saved until you confirm the preview below.</p>

            {impPreview && (
              <div className="space-y-4" data-testid="import-preview">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-1 font-medium">{impPreview.new.length} new</span>
                  <span className="bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2.5 py-1 font-medium">{impPreview.duplicates.length} duplicate{impPreview.duplicates.length === 1 ? "" : "s"}</span>
                  {impPreview.errors.length > 0 && <span className="bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2.5 py-1 font-medium">{impPreview.errors.length} error{impPreview.errors.length === 1 ? "" : "s"}</span>}
                </div>

                {impPreview.errors.length > 0 && (
                  <div className="border border-rose-200 bg-rose-50 rounded-md px-3 py-2 text-xs text-rose-700 space-y-0.5">
                    {impPreview.errors.map((e, i) => <div key={i}>Row {e.row}: {e.error}</div>)}
                  </div>
                )}

                {impPreview.new.length > 0 && (
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">New customers</div>
                    <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-40 overflow-y-auto">
                      {impPreview.new.map((r, i) => (
                        <div key={i} className="px-3 py-1.5 text-sm text-slate-700 flex justify-between">
                          <span>{r.company_name}</span>
                          <span className="text-xs text-slate-400">{r.billing_city}{r.gst_number ? ` · ${r.gst_number}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {impPreview.duplicates.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Duplicates found — choose which to keep</div>
                    {impPreview.duplicates.map((d) => (
                      <div key={d.existing_id} className="border border-amber-200 bg-amber-50/50 rounded-md p-3 space-y-2" data-testid={`import-dup-${d.existing_id}`}>
                        <div className="text-sm font-semibold text-slate-800">{d.existing_name}</div>
                        {d.changes.length ? (
                          <table className="w-full text-xs">
                            <thead><tr className="text-slate-400 text-left"><th className="py-1 font-medium">Field</th><th className="py-1 font-medium">Current</th><th className="py-1 font-medium">In file</th></tr></thead>
                            <tbody>
                              {d.changes.map((ch) => (
                                <tr key={ch.field} className="border-t border-amber-100">
                                  <td className="py-1 text-slate-500">{ch.field.replaceAll("_", " ")}</td>
                                  <td className="py-1 text-slate-700">{ch.old || <span className="text-slate-300">—</span>}</td>
                                  <td className="py-1 font-medium text-slate-900">{ch.new}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : <p className="text-xs text-slate-500">Identical to the existing record — nothing to change.</p>}
                        {d.changes.length > 0 && (
                          <div className="flex gap-4 text-xs pt-1">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="radio" name={`res-${d.existing_id}`} checked={impRes[d.existing_id] === "keep"}
                                     onChange={() => setImpRes((m) => ({ ...m, [d.existing_id]: "keep" }))} data-testid={`import-keep-${d.existing_id}`} />
                              Keep existing
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="radio" name={`res-${d.existing_id}`} checked={impRes[d.existing_id] === "replace"}
                                     onChange={() => setImpRes((m) => ({ ...m, [d.existing_id]: "replace" }))} data-testid={`import-replace-${d.existing_id}`} />
                              Use values from file
                            </label>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <Button onClick={commitImport} disabled={impBusy || (impPreview.new.length === 0 && !impPreview.duplicates.some((d) => impRes[d.existing_id] === "replace"))}
                        className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="import-commit">
                  {impBusy ? "Importing…" : `Import ${impPreview.new.length} new${impPreview.duplicates.filter((d) => impRes[d.existing_id] === "replace").length ? ` + update ${impPreview.duplicates.filter((d) => impRes[d.existing_id] === "replace").length}` : ""}`}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddressBlock({ title, prefix, form, setForm, action }) {
  const get = (k) => form[`${prefix}_${k}`];
  const set = (k, v) => setForm((f) => ({ ...f, [`${prefix}_${k}`]: v }));

  const onPincode = async (v) => {
    const pin = v.replace(/\D/g, "").slice(0, 6);
    set("pincode", pin);
    if (pin.length === 6) {
      try {
        const r = await api.get(`/utils/pincode/${pin}`);
        if (r.data.found) {
          setForm((f) => ({ ...f, [`${prefix}_state`]: r.data.state, [`${prefix}_city`]: r.data.city, [`${prefix}_pincode`]: pin }));
        } else {
          toast.warning("Pincode not found — enter City & State manually");
        }
      } catch { /* offline lookup failure, manual entry */ }
    }
  };

  return (
    <div className="sm:col-span-2 border border-slate-200 rounded-md p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{title}</p>
        {action}
      </div>
      <Input placeholder="Address Line 1" value={get("line1")} onChange={(e) => set("line1", e.target.value)} data-testid={`c-${prefix}-line1`} />
      <Input placeholder="Address Line 2" value={get("line2")} onChange={(e) => set("line2", e.target.value)} data-testid={`c-${prefix}-line2`} />
      <div className="grid grid-cols-3 gap-2">
        <Input placeholder="Pincode" value={get("pincode")} onChange={(e) => onPincode(e.target.value)} data-testid={`c-${prefix}-pincode`} />
        <Input placeholder="City" value={get("city")} onChange={(e) => set("city", e.target.value)} data-testid={`c-${prefix}-city`} />
        <Input placeholder="State" value={get("state")} onChange={(e) => set("state", e.target.value)} data-testid={`c-${prefix}-state`} />
      </div>
    </div>
  );
}

function Field({ label, v, onChange, tid, type = "text" }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label>
      <Input value={v} onChange={(e) => onChange(e.target.value)} className="mt-1.5" data-testid={tid} type={type} />
    </div>
  );
}
